// src/components/loginAWS/Profile/Profile.js
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../routes/AuthContext';
import {
  CognitoUserPool,
  CognitoUserAttribute
} from 'amazon-cognito-identity-js';
import cognitoConfig from '../cognitoConfig.json';
import './Profile.css';

const userPool = new CognitoUserPool({
  UserPoolId: cognitoConfig.UserPoolId,
  ClientId: cognitoConfig.ClientId
});

// API to sync phone changes into DynamoDB via GSI on PhoneNumber
const CHANGE_PHONE_ENDPOINT =
  'https://85pdnha5ei.execute-api.us-east-1.amazonaws.com/changePhoneNum';

async function updateDynamoPhone(currentPhone, newPhone) {
  try {
    const res = await fetch(CHANGE_PHONE_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ currentPhone, newPhone })
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
    return { ok: true, data };
  } catch (err) {
    return { ok: false, error: err.message || 'Request failed' };
  }
}

const Profile = () => {
  const { authUser, setAuthUser } = useAuth();
  const payload = authUser?.idToken?.payload;

  const deriveDataFromPayload = (p) => {
    if (!p) {
      return {
        customerId: 'N/A',
        email: '',
        username: '',
        given_name: '',
        family_name: '',
        phone_number: '',
        birthdate: '',
        address: '',
        sms_optin: false
      };
    }
    const addr =
      typeof p.address === 'string'
        ? p.address
        : p.address?.formatted ||
          p.address?.street_address ||
          '';
    // Parse sms_optin - it's stored as string "true" or "false" in Cognito
    const smsOptInValue = p['custom:sms_optin'] || 'false';
    const sms_optin = smsOptInValue.toLowerCase() === 'true';
    return {
      customerId: p['custom:User_ID'] || 'N/A',
      email: p.email || '',
      username: p['cognito:username'] || '',
      given_name: p.given_name || '',
      family_name: p.family_name || '',
      phone_number: p.phone_number || '',
      birthdate: p.birthdate || '',
      address: addr || '',
      sms_optin
    };
  };

  const [formData, setFormData] = useState(deriveDataFromPayload(payload));
  const [originalData, setOriginalData] = useState(
    deriveDataFromPayload(payload)
  );

  const [editMode, setEditMode] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');

  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [passwordMessage, setPasswordMessage] = useState('');

  useEffect(() => {
    if (!payload) return;
    const fresh = deriveDataFromPayload(payload);
    setFormData(fresh);
    setOriginalData(fresh);
  }, [payload]);

  useEffect(() => {
    if (editMode) {
      const el = document.getElementById('profile-address-textarea');
      if (el) {
        el.style.height = 'auto';
        el.style.height = el.scrollHeight + 'px';
      }
    }
  }, [editMode]);

  const handleFieldChange = (key, value) => {
    setFormData((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const handleAddressChange = (e) => {
    const val = e.target.value;
    handleFieldChange('address', val);
    e.target.style.height = 'auto';
    e.target.style.height = e.target.scrollHeight + 'px';
  };

  const persistSessionToAppState = (session) => {
    const newIdTokenObj = session.getIdToken();
    const newAccessTokenObj = session.getAccessToken();
    const newRefreshTokenObj = session.getRefreshToken();

    const newJwt = newIdTokenObj.getJwtToken();
    const newPayload = newIdTokenObj.decodePayload();

    try {
      localStorage.setItem(
        'authUser',
        JSON.stringify({
          idToken: newJwt,
          accessToken: newAccessTokenObj.getJwtToken(),
          refreshToken: newRefreshTokenObj.getToken(),
          payload: newPayload
        })
      );
    } catch (e) {
      console.warn('localStorage write failed:', e);
    }

    if (setAuthUser) {
      setAuthUser({
        getIdToken: () => newIdTokenObj,
        getAccessToken: () => newAccessTokenObj,
        getRefreshToken: () => newRefreshTokenObj,
        idToken: newIdTokenObj,
        accessToken: newAccessTokenObj,
        refreshToken: newRefreshTokenObj
      });
    }

    const freshData = deriveDataFromPayload(newPayload);
    setFormData(freshData);
    setOriginalData(freshData);
  };

  const refreshTokensFromCognito = (cognitoUser) => {
    cognitoUser.getSession((err, session) => {
      if (err || !session?.isValid()) {
        console.warn('getSession failed after update:', err);
        setEditMode(false);
        setSaveMessage('Profile saved, but session refresh failed.');
        return;
      }
      const refreshToken = session.getRefreshToken();
      cognitoUser.refreshSession(refreshToken, (err2, newSession) => {
        if (err2 || !newSession) {
          console.warn('refreshSession failed:', err2);
          setEditMode(false);
          setSaveMessage('Profile saved, but token refresh failed.');
          return;
        }
        persistSessionToAppState(newSession);
        setEditMode(false);
        setSaveMessage('Profile saved.');
      });
    });
  };

  const saveProfileToCognito = () => {
    setSaveMessage('');

    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      setSaveMessage('Session not found. Please log in again.');
      setEditMode(false);
      setFormData(originalData);
      return;
    }

    const phoneChanged =
      (formData.phone_number || '') !== (originalData.phone_number || '');
    const oldPhone = originalData.phone_number || '';
    const newPhone = formData.phone_number || '';

    cognitoUser.getSession((err, session) => {
      if (err || !session?.isValid()) {
        console.error('Session error:', err);
        setSaveMessage('Session expired. Please log in again.');
        setEditMode(false);
        setFormData(originalData);
        return;
      }

      const updatableKeys = [
        'given_name',
        'family_name',
        'phone_number',
        'birthdate',
        'address',
        'email'
      ];

      // Handle custom attributes separately (they use 'custom:' prefix)
      const customAttributes = [];
      const smsOptInChanged = Boolean(formData.sms_optin) !== Boolean(originalData.sms_optin);
      if (smsOptInChanged) {
        customAttributes.push(
          new CognitoUserAttribute({
            Name: 'custom:sms_optin',
            Value: formData.sms_optin ? 'true' : 'false'
          })
        );
      }

      const updates = [];
      updatableKeys.forEach((key) => {
        if ((formData[key] || '') !== (originalData[key] || '')) {
          updates.push(
            new CognitoUserAttribute({
              Name: key,
              Value: formData[key] || ''
            })
          );
        }
      });

      // Helper function to finish the update process
      const finishUpdate = () => {
        (async () => {
          if (phoneChanged && oldPhone && newPhone) {
            const r = await updateDynamoPhone(oldPhone, newPhone);
            if (!r.ok) {
              setSaveMessage(
                `Profile saved in Cognito. Phone sync failed: ${r.error}`
              );
            }
          }
          refreshTokensFromCognito(cognitoUser);
        })();
      };

      // Check if there are any changes
      if (updates.length === 0 && customAttributes.length === 0) {
        setOriginalData(formData);
        setEditMode(false);
        setSaveMessage('No changes.');
        return;
      }

      // Update standard attributes first, then custom attributes
      if (updates.length > 0) {
        cognitoUser.updateAttributes(updates, (err2) => {
          if (err2) {
            console.error('updateAttributes failed:', err2);
            setSaveMessage('Update failed.');
            setFormData(originalData);
            setEditMode(false);
            return;
          }

          // Then update custom attributes if any
          if (customAttributes.length > 0) {
            cognitoUser.updateAttributes(customAttributes, (err3) => {
              if (err3) {
                console.error('updateAttributes (custom) failed:', err3);
                setSaveMessage('Profile saved, but SMS opt-in update failed.');
              }
              finishUpdate();
            });
          } else {
            finishUpdate();
          }
        });
      } else if (customAttributes.length > 0) {
        // Only custom attributes to update
        cognitoUser.updateAttributes(customAttributes, (err3) => {
          if (err3) {
            console.error('updateAttributes (custom) failed:', err3);
            setSaveMessage('SMS opt-in update failed.');
            setFormData(originalData);
            setEditMode(false);
            return;
          }
          finishUpdate();
        });
      }
    });
  };

  const handlePasswordChange = (e) => {
    e.preventDefault();

    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) {
      setPasswordMessage('User session not found. Please log in again.');
      return;
    }

    cognitoUser.getSession((err, session) => {
      if (err || !session.isValid()) {
        console.error('Session error:', err);
        setPasswordMessage('Session expired. Please log in again.');
        return;
      }

      cognitoUser.changePassword(oldPassword, newPassword, (err2, result) => {
        if (err2) {
          console.error('Password change failed:', err2);
          setPasswordMessage(
            'Password change failed. Please check your old password.'
          );
        } else {
          console.log('Password change success:', result);
          setPasswordMessage('Password changed successfully.');
        }
      });
    });
  };

  const renderField = (label, key, alwaysReadOnly = false) => {
    const value = formData[key] || '';
    const displayVal = value !== '' ? value : 'N/A';

    if (editMode && !alwaysReadOnly) {
      if (key === 'address') {
        return (
          <div className="col-12 col-lg-6 d-flex align-items-start mb-2">
            <strong style={{ width: '130px' }}>{label}:</strong>
            <textarea
              id="profile-address-textarea"
              className="input-text"
              style={{
                marginLeft: '10px',
                flex: 1,
                resize: 'none',
                overflow: 'hidden',
                minHeight: '36px',
                lineHeight: '1.4'
              }}
              value={value}
              onChange={handleAddressChange}
            />
          </div>
        );
      }

      return (
        <div className="col-12 col-lg-6 d-flex align-items-center mb-2">
          <strong style={{ width: '130px' }}>{label}:</strong>
          <input
            type="text"
            className="input-text"
            style={{ marginLeft: '10px', flex: 1 }}
            value={value}
            onChange={(e) => handleFieldChange(key, e.target.value)}
          />
        </div>
      );
    }

    const readOnlyStyle =
      key === 'address'
        ? {
            marginLeft: '10px',
            flex: 1,
            height: 'auto',
            minHeight: '36px',
            lineHeight: '1.4',
            whiteSpace: 'pre-wrap',
            alignItems: 'flex-start'
          }
        : {
            marginLeft: '10px',
            flex: 1
          };

    return (
      <div className="col-12 col-lg-6 d-flex align-items-start mb-2">
        <strong style={{ width: '130px' }}>{label}:</strong>
        <div className="input-text read-only" style={readOnlyStyle}>
          {displayVal}
        </div>
      </div>
    );
  };

  if (!payload) {
    return (
      <section className="contact-section">
        <div className="container">
          <div className="row">
            <div className="col-12">
              <h2 className="section-title">Unauthorized</h2>
              <p>You must be logged in to view your profile.</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="contact-section">
      <div className="container">
        <div className="row">
          {/* left column */}
          <div className="col-lg-4">
            <div className="contact-box">
              <div className="section-tagline">YOUR PROFILE</div>
              <h1 className="section-title">
                {formData.given_name || ''} {formData.family_name || ''}
              </h1>
              <p>This is your account information retrieved from Cognito.</p>

              {!editMode && (
                <div className="mt-3">
                  <button
                    className="btn btn-secondary"
                    onClick={() => {
                      setSaveMessage('');
                      setEditMode(true);
                    }}
                  >
                    Edit Profile
                  </button>
                </div>
              )}

              {saveMessage && (
                <div
                  style={{
                    marginTop: '10px',
                    fontSize: '14px',
                    color:
                      saveMessage === 'Profile saved.' ||
                      saveMessage === 'No changes.'
                        ? 'green'
                        : 'red'
                  }}
                >
                  {saveMessage}
                </div>
              )}
            </div>
          </div>

          {/* right column */}
          <div className="col-lg-8">
            <div className="contact-form">
              <div className="row row-gutter-10">
                {renderField('Customer ID', 'customerId', true)}
                {renderField('Email', 'email')}
                {renderField('Username', 'username', true)}
                {renderField('First Name', 'given_name')}
                {renderField('Last Name', 'family_name')}
                {renderField('Phone Number', 'phone_number')}
                {renderField('Birthdate', 'birthdate')}
                {renderField('Address', 'address')}

                {/* SMS Opt-in Checkbox */}
                <div className="col-12 mb-3">
                  <div className="d-flex align-items-start">
                    <strong style={{ width: '130px', marginTop: '8px' }}>SMS Messaging:</strong>
                    <div style={{ marginLeft: '10px', flex: 1 }}>
                      <label style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer', margin: 0 }}>
                        <input
                          type="checkbox"
                          checked={formData.sms_optin || false}
                          onChange={(e) => handleFieldChange('sms_optin', e.target.checked)}
                          disabled={!editMode}
                          style={{ marginTop: '4px', marginRight: '10px', flexShrink: 0 }}
                        />
                        <div style={{ fontSize: '14px', lineHeight: '1.5' }}>
                          <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                            Opt in to receive SMS messages
                          </div>
                          <div style={{ color: '#666', fontSize: '13px' }}>
                            By checking this box, you consent to receive SMS messages from us. Message and data rates may apply. 
                            You can opt out at any time by unchecking this box. Reply STOP to opt out or HELP for help.
                          </div>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="col-12 mt-3">
                  {!showPasswordForm ? (
                    <>
                      <div
                        style={{
                          display: 'flex',
                          gap: '10px',
                          flexWrap: 'wrap'
                        }}
                      >
                        {editMode && (
                          <button
                            className="btn btn-secondary"
                            style={{
                              backgroundColor: '#e75934',
                              borderColor: '#e75934',
                              color: '#fff'
                            }}
                            onClick={saveProfileToCognito}
                          >
                            Save
                          </button>
                        )}
                        <button
                          className="btn btn-secondary"
                          onClick={() => {
                            setShowPasswordForm(true);
                          }}
                        >
                          Change Password
                        </button>
                      </div>

                      {passwordMessage && (
                        <div style={{ marginTop: '10px' }}>
                          <p style={{ color: 'green' }}>{passwordMessage}</p>
                        </div>
                      )}
                    </>
                  ) : (
                    <form onSubmit={handlePasswordChange}>
                      <div className="row row-gutter-10">
                        <div className="col-12">
                          <p>
                            Your account requires your current password and a new password.
                          </p>
                        </div>

                        <div className="col-12 col-lg-6">
                          <input
                            type="password"
                            className="input-text"
                            placeholder="Current Password"
                            value={oldPassword}
                            onChange={(e) => setOldPassword(e.target.value)}
                            required
                          />
                        </div>

                        <div className="col-12 col-lg-6">
                          <input
                            type="password"
                            className="input-text"
                            placeholder="New Password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                          />
                        </div>

                        <div className="col-12">
                          <button type="submit" className="btn btn-primary">
                            Change Password
                          </button>
                        </div>

                        {passwordMessage && (
                          <div className="col-12">
                            <p style={{ color: 'green' }}>{passwordMessage}</p>
                          </div>
                        )}
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Profile;
